import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaHardHat, FaClock, FaCheckCircle, FaSpinner, FaBuilding, FaCalendarAlt } from "react-icons/fa";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useBuilding } from "../context/BuildingContext";
import { tolerantId } from "../utils/firestoreHelpers";
import { formatDateLocalized } from "../utils/formatters";
import EmptyStateComingSoon from "../components/EmptyStateComingSoon";
import { useIsMobile } from "../hooks/useResponsive";
import {
  AppScreen,
  AppHeader,
  AppCard,
  AppEmptyState,
  Badge,
  IconBox,
  SectionLabel,
} from "../components/app";
import { appColors, appType, appRadius, appSpacing } from "../utils/appTheme";

// Tono de estado (paleta app) a partir de la clave de estado (mobile)
const statusToneKey = (estado) => {
  switch ((estado || "").toLowerCase()) {
    case "en progreso":
      return "blue";
    case "pendiente":
      return "amber";
    case "finalizado":
      return "green";
    default:
      return "ink3";
  }
};

// Color de la paleta app a partir de la clave de estado (mobile)
const statusToneColor = (estado) => {
  switch ((estado || "").toLowerCase()) {
    case "en progreso":
      return appColors.blue;
    case "pendiente":
      return appColors.amber;
    case "finalizado":
      return appColors.green;
    default:
      return appColors.ink3;
  }
};

const ObrasYProyectos = ({ setSelected, goBack }) => {
  const { t } = useTranslation("obras");
  const isMobile = useIsMobile();
  const { selectedBuilding } = useBuilding();
  const [proyectos, setProyectos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProyectos = async () => {
      if (!selectedBuilding?.buildingid) {
        setLoading(false);
        return;
      }
      try {
        const q = query(
          collection(db, "projects"),
          where("buildingid", "in", tolerantId(selectedBuilding.buildingid))
        );
        const snap = await getDocs(q);
        setProyectos(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error("Error al cargar proyectos:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProyectos();
  }, [selectedBuilding]);

  const getStatusStyle = (estado) => {
    switch ((estado || "").toLowerCase()) {
      case "en progreso":
        return { backgroundColor: "#e8f4ff", color: "#0080ff", icon: <FaSpinner /> };
      case "pendiente":
        return { backgroundColor: "#fef3c7", color: "#d97706", icon: <FaClock /> };
      case "finalizado":
        return { backgroundColor: "#dcfce7", color: "#16a34a", icon: <FaCheckCircle /> };
      default:
        return { backgroundColor: "#f3f4f6", color: "#6b7280", icon: <FaClock /> };
    }
  };

  const formatDate = (date) => {
    if (!date) return "";
    if (date?.seconds) return formatDateLocalized(new Date(date.seconds * 1000));
    return String(date);
  };

  if (loading) {
    if (isMobile) {
      return (
        <AppScreen
          header={
            <AppHeader
              title={t("title")}
              subtitle={t("subtitle")}
              onBack={goBack}
            />
          }
        >
          <p style={{ ...appType.bodySm, textAlign: "center", padding: "40px 0" }}>
            {t("loading")}
          </p>
        </AppScreen>
      );
    }
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.headerIcon}><FaHardHat /></div>
          <div style={styles.headerInfo}>
            <h1 style={styles.title}>{t("title")}</h1>
            <p style={styles.subtitle}>{t("subtitleDesktop")}</p>
          </div>
        </div>
        <p style={{ textAlign: "center", color: "#6b7280", padding: "40px" }}>{t("loading")}</p>
      </div>
    );
  }

  // ─── Contenido mobile: estilo app "Radial" ─────────────────────────────────
  if (isMobile) {
    // Resumen read-only derivado del estado ya calculado (sin queries nuevas).
    const norm = (p) => (p.status || p.estado || "Pendiente").toLowerCase();
    const resumen = [
      { key: "en progreso", label: t("summary.inProgress"), color: appColors.blue, count: proyectos.filter((p) => norm(p) === "en progreso").length },
      { key: "pendiente", label: t("summary.pending"), color: appColors.amber, count: proyectos.filter((p) => norm(p) === "pendiente").length },
      { key: "finalizado", label: t("summary.finished"), color: appColors.green, count: proyectos.filter((p) => norm(p) === "finalizado").length },
    ];

    return (
      <AppScreen
        header={
          <AppHeader
            title={t("title")}
            subtitle={t("subtitle")}
            onBack={goBack}
          />
        }
      >
        {proyectos.length === 0 ? (
          <AppEmptyState
            icon={<FaHardHat />}
            title={t("empty.title")}
            description={t("empty.description")}
          />
        ) : (
          <>
            {/* Resumen: tiles verticales con línea de estado coloreada abajo */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: appSpacing.gridGap,
                marginBottom: appSpacing.section,
              }}
            >
              {resumen.map((r) => (
                <div
                  key={r.key}
                  style={{
                    background: appColors.surface,
                    border: `1px solid ${appColors.line2}`,
                    borderRadius: appRadius.tile,
                    boxShadow: "0 1px 2px rgba(18,20,26,.04), 0 6px 18px rgba(18,20,26,.06)",
                    padding: "14px 12px 0",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 4,
                    minWidth: 0,
                    overflow: "hidden",
                  }}
                >
                  <span style={{ ...appType.stat, fontSize: 22, color: appColors.ink, fontVariantNumeric: "tabular-nums" }}>
                    {r.count}
                  </span>
                  <span style={{ ...appType.statLabel, color: appColors.ink3, overflowWrap: "anywhere" }}>
                    {r.label}
                  </span>
                  <span
                    style={{
                      marginTop: 10,
                      alignSelf: "stretch",
                      height: 3,
                      borderRadius: 2,
                      background: r.count > 0 ? r.color : appColors.line2,
                    }}
                  />
                </div>
              ))}
            </div>

            <SectionLabel style={{ marginBottom: 12 }}>{t("registered")}</SectionLabel>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {proyectos.map((item) => {
                const estado = item.status || item.estado || t("status.pending");
                const tone = statusToneKey(item.status || item.estado);
                const toneColor = statusToneColor(item.status || item.estado);
                const empresa = item.company || item.empresa || t("noCompany");
                const finalizacion = formatDate(item.endDate || item.fechaFin);
                const descripcion = item.description || item.descripcion;
                return (
                  <AppCard
                    key={item.id}
                    accent="blue"
                    style={{ borderLeftColor: toneColor }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                      <IconBox icon={<FaHardHat />} color={toneColor} size={40} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 10,
                            flexWrap: "wrap",
                          }}
                        >
                          <span
                            style={{
                              ...appType.cardTitle,
                              flex: 1,
                              minWidth: 0,
                              overflowWrap: "anywhere",
                            }}
                          >
                            {item.title || item.titulo}
                          </span>
                          <Badge tone={tone} dot>{estado}</Badge>
                        </div>
                        {descripcion && (
                          <p
                            style={{
                              ...appType.body,
                              color: appColors.ink2,
                              margin: "8px 0 0",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {descripcion}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Footer en grilla: EMPRESA / FINALIZACIÓN (micro-labels) */}
                    <div
                      style={{
                        marginTop: 14,
                        paddingTop: 14,
                        borderTop: `1px solid ${appColors.line2}`,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 12,
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ ...appType.statLabel, color: appColors.ink3, marginBottom: 4 }}>
                          {t("fields.company")}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                          <FaBuilding style={{ color: appColors.blue, fontSize: 12, flexShrink: 0 }} />
                          <span style={{ ...appType.body, fontSize: 13.5, minWidth: 0, overflowWrap: "anywhere" }}>
                            {empresa}
                          </span>
                        </div>
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ ...appType.statLabel, color: appColors.ink3, marginBottom: 4 }}>
                          {t("fields.finishDate")}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                          <FaCalendarAlt style={{ color: appColors.blue, fontSize: 12, flexShrink: 0 }} />
                          <span style={{ ...appType.body, fontSize: 13.5, minWidth: 0, overflowWrap: "anywhere", fontVariantNumeric: "tabular-nums" }}>
                            {finalizacion || t("noDate")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </AppCard>
                );
              })}
            </div>
          </>
        )}
      </AppScreen>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerIcon}><FaHardHat /></div>
        <div style={styles.headerInfo}>
          <h1 style={styles.title}>{t("title")}</h1>
          <p style={styles.subtitle}>{t("subtitleDesktop")}</p>
        </div>
      </div>

      {proyectos.length === 0 ? (
        <EmptyStateComingSoon
          icon={<FaHardHat />}
          title={t("empty.title")}
          description={t("empty.descriptionDesktop")}
        />
      ) : (
        <div style={styles.grid}>
          {proyectos.map((item) => {
            const statusStyle = getStatusStyle(item.status || item.estado);
            return (
              <div key={item.id} style={styles.card}>
                <div style={styles.cardTop}>
                  <h3 style={styles.cardTitle}>{item.title || item.titulo}</h3>
                  <span style={{
                    ...styles.statusBadge,
                    backgroundColor: statusStyle.backgroundColor,
                    color: statusStyle.color,
                  }}>
                    {statusStyle.icon}
                    {item.status || item.estado || t("status.pending")}
                  </span>
                </div>
                <p style={styles.description}>{item.description || item.descripcion}</p>
                <div style={styles.detailsContainer}>
                  <div style={styles.detailRow}>
                    <FaBuilding style={styles.detailIcon} />
                    <span style={styles.detailText}>{item.company || item.empresa || t("noCompany")}</span>
                  </div>
                  <div style={styles.detailRow}>
                    <FaCalendarAlt style={styles.detailIcon} />
                    <span style={styles.detailText}>{t("finishedPrefix")} {formatDate(item.endDate || item.fechaFin)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: "24px",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
    backgroundColor: "#f5f7fa",
    minHeight: "100vh",
  },
  header: {
    background: "white", borderRadius: "12px", padding: "16px 20px", marginBottom: "20px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)", border: "1px solid #e5e7eb",
    display: "flex", alignItems: "center", gap: "14px",
  },
  headerIcon: {
    width: "42px", height: "42px", borderRadius: "10px", backgroundColor: "#0080ff",
    color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px",
  },
  headerInfo: { flex: 1 },
  title: { fontSize: "20px", fontWeight: 400, margin: 0, color: "#0080ff" },
  subtitle: { fontSize: "13px", color: "#6b7280", margin: "2px 0 0 0", fontWeight: 400 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "20px" },
  card: {
    backgroundColor: "#fff", border: "1px solid #e5e7eb", borderRadius: "12px", padding: "24px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column",
  },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px", marginBottom: "12px" },
  cardTitle: { fontSize: "17px", fontWeight: 400, color: "#1a1a1a", margin: 0, flex: 1 },
  statusBadge: {
    padding: "5px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: 400,
    display: "flex", alignItems: "center", gap: "5px", whiteSpace: "nowrap",
  },
  description: { fontSize: "14px", color: "#4b5563", marginBottom: "16px", lineHeight: "1.6", flex: 1 },
  detailsContainer: { display: "flex", flexDirection: "column", gap: "8px", paddingTop: "16px", borderTop: "1px solid #e5e7eb" },
  detailRow: { display: "flex", alignItems: "center", gap: "10px" },
  detailIcon: { color: "#0080ff", fontSize: "14px", width: "16px" },
  detailText: { fontSize: "13px", color: "#4b5563" },
};

export default ObrasYProyectos;
